import React, { memo } from 'react';
import { useMutation } from 'react-query';
import styled from 'styled-components';

import { useToast } from '@raptormaps/toast';

import {
  getAddOrgPermissionMutation,
  getRemoveOrgPermissionMutation,
  getUpdateOrgPermissionMutation,
  OrgPermission,
  PermissionRole,
} from '../../api/services/org-permissions';
import theme from '@raptormaps/theme';

const CapitalizedSelect = styled.select`
  height: 40px;
  background-color: ${theme.colors.white};
  border: 1px solid;
  border-color: ${theme.colors.neutral_300};
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 16px;

  > span {
    text-transform: capitalize;
  }
`;

interface RaptorSolarRolePickerProps {
  orgId: number;
  auth0UserId: string;
  permissionId: OrgPermission['id'] | null;
  role: OrgPermission['role_id'] | null;
  isRefetching: boolean;
}

const RaptorSolarRolePickerComponent = ({
  orgId,
  auth0UserId,
  permissionId,
  role,
  isRefetching,
}: RaptorSolarRolePickerProps) => {
  const toast = useToast();

  const { mutateAsync: updateRole, isLoading: isUpdatingRole } = useMutation(
    getUpdateOrgPermissionMutation(orgId, permissionId ?? NaN),
  );
  const { mutateAsync: addRole, isLoading: isAddingRole } = useMutation(
    getAddOrgPermissionMutation(orgId, auth0UserId),
  );
  const { mutateAsync: removeRole, isLoading: isRemovingRole } = useMutation(
    getRemoveOrgPermissionMutation(orgId, permissionId ?? NaN),
  );

  const handleChange = (newRole: PermissionRole | null) => {
    let promise: Promise<unknown> | null = null;

    if (permissionId !== null) {
      promise = newRole !== null ? updateRole(newRole) : removeRole();
    } else if (newRole !== null) {
      promise = addRole(newRole);
    }

    if (promise) {
      toast.promise(promise, {
        loading: 'Updating user role...',
        success: 'User role updated!',
        error: 'Unable to update user role. Please try again.',
      });
    }
  };

  return (
    <CapitalizedSelect
      name={`${auth0UserId}-role-picker`}
      value={role ?? ''}
      onChange={e => {
        const value = e.target.value;
        handleChange(value.length > 0 ? (value as PermissionRole) : null);
      }}
      disabled={
        isUpdatingRole || isRemovingRole || isAddingRole || isRefetching
      }
    >
      <option value="">Unassigned</option>
      <option value="viewer">Viewer</option>
      <option value="admin">Admin</option>
    </CapitalizedSelect>
  );
};

export const RaptorSolarRolePicker = memo(RaptorSolarRolePickerComponent);
